import { useAuth } from '@clerk/react';
import { useFeatureFlagEnabled, usePostHog } from 'posthog-js/react';
import { useFeatureAccess } from '@/hooks/use-feature-access.hook';

export const NATIVE_SEQUENCING_FLAG = 'native_sequencing_v1';

/** The frontend-only build is reviewable on loopback development servers. */
export function isLocalSequencingDevelopment(
  isDevelopment: boolean,
  hostname: string
): boolean {
  return (
    isDevelopment &&
    ['localhost', '127.0.0.1', '::1', '[::1]'].includes(hostname)
  );
}

/**
 * Organization rollout remains off until explicitly enabled in PostHog.
 * The local exception exposes fictional UI only; it never bypasses Clerk,
 * changes entitlements, or enables a backend API.
 */
export function useSequencingAccess() {
  return useOrgFlagAccess(NATIVE_SEQUENCING_FLAG);
}

/** LinkedIn steps and accounts, as the plan allows. */
export function useLinkedInAccess(): boolean {
  const { actions, isReady } = useFeatureAccess();
  return isReady !== false && (actions?.linkedin ?? true);
}

function useOrgFlagAccess(key: string) {
  const flag = useFeatureFlagEnabled(key);
  const posthog = usePostHog();
  const { orgId, isLoaded } = useAuth();
  const local = isLocalSequencingDevelopment(
    import.meta.env.DEV,
    typeof window === 'undefined' ? '' : window.location.hostname
  );
  const groupMatches = Boolean(
    orgId && posthog?.getGroups().organization === orgId
  );
  return {
    enabled: local || (isLoaded && groupMatches && flag === true),
    isReady: local || (isLoaded && flag !== undefined),
    local,
  };
}
